import { useState } from "react";
import { toast } from "sonner";
import { Briefcase, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { SellerAuthGate } from "./SellerAuthGate";
import {
  US_STATES, BIZ_INDUSTRIES, PublicShell, Field, SubmissionResult, inputCls,
  RoleAttestation, TimelineField, ReviewProcessNotice, AddressVisibilityNote,
} from "./_shared";

export default function SubmitBusiness() {
  const [f, setF] = useState({
    business_name: "", industry: "", state: "", city: "",
    no_physical_location: false, street_address: "", suite: "", zip_code: "",
    asking_price: "", annual_revenue: "", annual_cash_flow: "", years_in_operation: "",
    reason_for_selling: "", seller_financing_available: false, seller_financing_terms: "",
    down_payment_pct: "",
    contact_name: "", contact_role: "owner", contact_phone: "",
    listing_agreement_accepted: false,
    owner_attestation: false, representative_attestation: false,
    timeline: "exploring",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.listing_agreement_accepted) {
      toast.error("Please confirm the listing agreement to submit.");
      return;
    }
    if (f.contact_role === "owner" && !f.owner_attestation) {
      toast.error("Please confirm the owner attestation to submit.");
      return;
    }
    if ((f.contact_role === "broker" || f.contact_role === "other") && !f.representative_attestation) {
      toast.error("Please confirm the authorized-representative attestation to submit.");
      return;
    }
    if (!f.no_physical_location && (!f.street_address || f.street_address.trim().length < 4)) {
      toast.error("Please enter a street address, or check \"No physical location\".");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        asking_price: Number(f.asking_price) || 0,
        annual_revenue: Number(f.annual_revenue) || 0,
        annual_cash_flow: Number(f.annual_cash_flow) || 0,
        years_in_operation: parseInt(f.years_in_operation || "0", 10) || 0,
        down_payment_pct: f.down_payment_pct === "" ? null : Number(f.down_payment_pct),
      };
      const r = await api.post("/listings/submit/business", payload);
      const addressValue = f.no_physical_location ? `${f.city}, ${f.state} ${f.zip_code}`.trim() : f.street_address.trim();
      if (r.data?.duplicate) {
        toast.warning("This business looks like it is already in our system. We have linked your submission.");
      } else {
        toast.success("Listing received");
      }
      setResult({
        oid: r.data?.oid,
        title: f.business_name,
        address: addressValue,
        confirmationEmailSent: r.data?.confirmation_email_sent,
      });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <PublicShell seoRoute="/submit-business" title="Submit a Business Listing" testid="submit-business-page">
        <SubmissionResult
          testid="thank-you-business"
          title={result.title}
          address={result.address}
          oid={result.oid}
          confirmationEmailSent={result.confirmationEmailSent}
        />
      </PublicShell>
    );
  }

  return (
    <PublicShell
      seoRoute="/submit-business"
      title="Submit a Business Listing to Acquigine"
      subtitle="Submit a profitable business for review by our verified network of acquirers who work with seller financing and creative structures. Every submission is reviewed by our team before it becomes visible to any acquirer, and may be declined."
      testid="submit-business-page"
    >
      <SellerAuthGate>
        <form onSubmit={submit} className="ae-card p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-white/8">
            <div className="h-9 w-9 rounded-sm bg-emerald-500/15 flex items-center justify-center"><Briefcase className="h-4 w-4 text-emerald-400" /></div>
            <div className="text-sm font-medium text-slate-100">Business details</div>
          </div>

          <Field label="Business name" required>
            <input className={inputCls} required value={f.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="biz-name" />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Industry">
              <select className={inputCls} value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="biz-industry">
                <option value="">Select industry</option>
                {BIZ_INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Years in operation" required>
              <input className={inputCls} type="number" min="0" required value={f.years_in_operation} onChange={(e) => set("years_in_operation", e.target.value)} data-testid="biz-years" />
            </Field>
          </div>

          <div className="pt-3 border-t border-white/8 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={f.no_physical_location}
                onChange={(e) => set("no_physical_location", e.target.checked)}
                data-testid="biz-no-physical-location"
              />
              <span className="text-sm text-slate-200">No physical location (online or home-based business)</span>
            </label>

            {!f.no_physical_location && (
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Field label="Street address" required>
                    <input className={inputCls} required value={f.street_address} onChange={(e) => set("street_address", e.target.value)} data-testid="biz-street-address" />
                  </Field>
                </div>
                <Field label="Suite / unit">
                  <input className={inputCls} value={f.suite} onChange={(e) => set("suite", e.target.value)} data-testid="biz-suite" />
                </Field>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <Field label="City" required>
                <input className={inputCls} required value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="biz-city" />
              </Field>
              <Field label="State" required>
                <select className={inputCls} required value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="biz-state">
                  <option value="">Select state</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="ZIP code" required>
                <input className={inputCls} required value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="biz-zip" />
              </Field>
            </div>
            <AddressVisibilityNote />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Asking price ($)" required>
              <input className={inputCls} type="number" min="0" required value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} data-testid="biz-asking" />
            </Field>
            <Field label="Annual gross revenue ($)" required>
              <input className={inputCls} type="number" min="0" required value={f.annual_revenue} onChange={(e) => set("annual_revenue", e.target.value)} data-testid="biz-revenue" />
            </Field>
            <Field label="Annual cash flow / SDE ($)" required>
              <input className={inputCls} type="number" min="0" required value={f.annual_cash_flow} onChange={(e) => set("annual_cash_flow", e.target.value)} data-testid="biz-cash-flow" />
            </Field>
          </div>

          <Field label="Reason for selling" hint="Optional. Used to identify motivation signals.">
            <textarea className={inputCls} rows={2} value={f.reason_for_selling} onChange={(e) => set("reason_for_selling", e.target.value)} data-testid="biz-reason" />
          </Field>

          <div className="pt-3 border-t border-white/8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.seller_financing_available} onChange={(e) => set("seller_financing_available", e.target.checked)} data-testid="biz-sf-yes" />
              <span className="text-sm text-slate-200">Seller financing available</span>
            </label>
            {f.seller_financing_available && (
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <Field label="Terms (if known)" hint='e.g. "10% down, 6% interest, 10yr"'>
                  <textarea className={inputCls} rows={2} value={f.seller_financing_terms} onChange={(e) => set("seller_financing_terms", e.target.value)} data-testid="biz-sf-terms" />
                </Field>
                <Field label="Down payment required (%)">
                  <input className={inputCls} type="number" min="0" max="100" value={f.down_payment_pct} onChange={(e) => set("down_payment_pct", e.target.value)} data-testid="biz-down-pct" />
                </Field>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-white/8 grid md:grid-cols-2 gap-4">
            <Field label="Your name" required>
              <input className={inputCls} required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="biz-contact-name" />
            </Field>
            <Field label="Phone">
              <input className={inputCls} value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="biz-contact-phone" />
            </Field>
          </div>

          <RoleAttestation
            role={f.contact_role}
            setRole={(v) => set("contact_role", v)}
            ownerAck={f.owner_attestation}
            setOwnerAck={(v) => set("owner_attestation", v)}
            repAck={f.representative_attestation}
            setRepAck={(v) => set("representative_attestation", v)}
            prefix="biz"
          />

          <div className="pt-2">
            <TimelineField value={f.timeline} onChange={(v) => set("timeline", v)} prefix="biz" />
          </div>

          <ReviewProcessNotice />

          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/10 cursor-pointer">
            <input type="checkbox" required checked={f.listing_agreement_accepted} onChange={(e) => set("listing_agreement_accepted", e.target.checked)} className="mt-1" data-testid="biz-agreement" />
            <span className="text-xs text-slate-300">
              I confirm I have the right to list this business and that the information I have provided is accurate.
            </span>
          </label>

          <button type="submit" disabled={busy} data-testid="biz-submit" className="btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Submitting..." : "Submit listing"}
          </button>
        </form>
      </SellerAuthGate>
    </PublicShell>
  );
}
